import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import axios from "axios";
import authService from "../services/AuthService";

interface SessionContextType {
  sessionTimeout: number;
  sessionExpires: string | null;
  isLoggedIn: boolean;
  isSessionValid: boolean;
  refreshSession: () => Promise<void>;
  login: () => void;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
  children: ReactNode;
}

const DEFAULT_SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 min, used only for the initial/unauthenticated state

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    authService.isLoggedIn(),
  );
  const [sessionTimeout, setSessionTimeout] = useState<number>(
    DEFAULT_SESSION_TIMEOUT_MS,
  );
  const [sessionExpires, setSessionExpires] = useState<string | null>(null);
  const [isSessionValid, setIsSessionValid] = useState<boolean>(true);

  const logout = useCallback(async () => {
    await authService.logout();
    setIsLoggedIn(false);
    setIsSessionValid(false);
    setSessionExpires(null);
  }, []);

  /**
   * Extends the user session and retrieves the new expiration time
   *
   * Sends a request to the server to indicate user activity and extend the session
   * Retrieves the new session expiration time from response header
   *
   * @returns {Promise<void>}
   * @throws {Error} When session is invalid, clears authentication data and redirects to login page
   */
  const refreshSession = useCallback(async () => {
    const url = "/api/v2/session";

    try {
      const response = await axios.get(url, {
        withCredentials: true,
      });

      //'RehaCAT-Session-Expires' header should be exposed via Access-Control-Expose-Headers
      const sessionExpiresHeader = response.headers["rehacat-session-expires"];
      const expiresAt = sessionExpiresHeader
        ? Date.parse(sessionExpiresHeader)
        : NaN;
      const timeoutMs = expiresAt - Date.now();

      if (sessionExpiresHeader && !isNaN(expiresAt) && timeoutMs > 0) {
        setSessionExpires(sessionExpiresHeader);
        setSessionTimeout(timeoutMs);
        setIsSessionValid(true);
        setIsLoggedIn(true);
        return;
      }

      //throw an error if server response didn't provide the valid expiry value
      console.warn("Session refresh response missing a valid expiry header.");
      throw new Error("Session refresh returned no valid expiry");
    } catch (error) {
      console.error("Session refresh failed:", error);
      await logout();
      throw error;
    }
  }, [logout]);

  //updates the context status after login/out
  const login = useCallback(() => {
    setIsLoggedIn(true);
    refreshSession();
  }, [refreshSession]);

  const value: SessionContextType = {
    sessionTimeout,
    sessionExpires,
    isLoggedIn,
    isSessionValid,
    refreshSession,
    login,
    logout,
  };

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

export default SessionContext;
